import { useState } from "react";
import { useParams } from "react-router-dom";
import { useInboundItem } from "../hooks/useInbound";
import { Button } from "@tonic/shared/components/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@tonic/shared/components/ui";
import { Badge } from "@tonic/shared/components/ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@tonic/shared/components/ui";
import { ArrowLeft, Package, ClipboardCheck, History } from "lucide-react";
import { AssignFlowDialog } from "./AssignFlowDialog";
import { InspectionForm } from "./InspectionForm";

export function InboundDetailPage() {
  const { id } = useParams();
  const { data: item, isLoading } = useInboundItem(id);
  const [isAssignFlowOpen, setIsAssignFlowOpen] = useState(false);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!item) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <Package className="h-12 w-12 text-muted-foreground mb-4" />
        <h2 className="text-2xl font-bold mb-2">Item Not Found</h2>
        <p className="text-muted-foreground mb-4">
          The inbound item you're looking for doesn't exist.
        </p>
        <Button variant="outline" onClick={() => window.history.back()}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to List
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.history.back()}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-3xl font-bold">Inbound Item {item.reference}</h1>
          </div>
          <p className="text-muted-foreground">
            Received {new Date(item.receivedAt).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={() => setIsAssignFlowOpen(true)}>
            Assign Flow
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge className="text-lg">{item.status}</Badge>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Origin</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge variant="outline">
              {item.origin === "store" ? "In-Store Drop-off" : "Mail Return"}
            </Badge>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Items</CardTitle>
          </CardHeader>
          <CardContent>
            <span className="text-2xl font-bold">{item.items.length}</span>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="items" className="space-y-4">
        <TabsList>
          <TabsTrigger value="items">
            <Package className="h-4 w-4 mr-2" />
            Items
          </TabsTrigger>
          <TabsTrigger value="inspection">
            <ClipboardCheck className="h-4 w-4 mr-2" />
            Inspection
          </TabsTrigger>
          <TabsTrigger value="history">
            <History className="h-4 w-4 mr-2" />
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="items" className="space-y-4">
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                {item.items.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between border-b last:border-0 pb-4 last:pb-0"
                  >
                    <div>
                      <p className="font-medium">{item.name}</p>
                      <p className="text-sm text-muted-foreground">{item.sku}</p>
                    </div>
                    <Badge variant="outline">{item.condition || "Pending"}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="inspection">
          <InspectionForm item={item} />
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                {item.history?.map((event, index) => (
                  <div
                    key={index}
                    className="flex items-start justify-between border-b last:border-0 pb-4 last:pb-0"
                  >
                    <div>
                      <p className="font-medium">{event.action}</p>
                      <p className="text-sm text-muted-foreground">
                        {event.description}
                      </p>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {new Date(event.timestamp).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <AssignFlowDialog
        open={isAssignFlowOpen}
        onOpenChange={setIsAssignFlowOpen}
        item={item}
      />
    </div>
  );
}
